/**
 * Адрес вкладки в строке браузера.
 *
 * Своими руками, без react-router: вкладок десяток, состояния у них нет, а
 * транспорт и так наш. Задача ровно одна — чтобы обновление страницы, закладка
 * и кнопка «назад» возвращали туда же, где владелец был.
 *
 * До сих пор вкладка жила только в памяти. Обновил страницу, читая матрицу
 * гипервизоров, — оказался в общей картине; прислал ссылку на топологию —
 * получатель открыл обзор. Мелочь ровно до того момента, когда консоль нужна в
 * разговоре: «посмотри вот тут» перестаёт работать.
 *
 * Адрес — источник правды, состояние — его отражение, а не наоборот. Поэтому
 * `popstate` слушается всегда: браузер меняет адрес мимо нас, и не заметить
 * этого значит показывать одно, а в строке иметь другое.
 */
import { useEffect, useState } from 'react';

/**
 * Вкладка → путь. Пути по-английски.
 *
 * Латиница по-русски — худший из вариантов: `/prilozheniya` не читается ни
 * русским, ни английским глазом, а угадать по нему раздел нельзя. Английские
 * слова короче, привычны в адресной строке и не заставляют мысленно
 * переводить туда-обратно.
 */
/**
 * Все вкладки консоли. Тип, а не просто список.
 *
 * `Record<Tab, string>` означает, что новую вкладку НЕЛЬЗЯ завести, не дав ей
 * пути: сборка не пройдёт. Так и вышло с восстановлением и помощником — их
 * добавили в приложение, а сюда забыли, и обе жили по адресу `/overview`:
 * ссылка на восстановление открывала общую картину, а «посмотри вот тут»
 * переставало работать ровно там, где нужнее всего.
 */
export type Tab =
  | 'overview' | 'services' | 'topology' | 'agents' | 'links' | 'storage' | 'minio'
  | 'names' | 'dns' | 'restore' | 'switchover' | 'campaigns'
  | 'events' | 'settings' | 'devices' | 'mcp';

const TABS: Record<Tab, string> = {
  overview: '/overview',
  services: '/applications',
  topology: '/topology',
  agents: '/agents',
  links: '/links',
  storage: '/storage',
  minio: '/minio',
  names: '/names',
  dns: '/dns',
  restore: '/restore',
  switchover: '/switchover',
  campaigns: '/campaigns',
  events: '/events',
  settings: '/settings',
  devices: '/keys',
  mcp: '/assistant',
};

/**
 * Что ещё принимаем — корень и прежние транслитерации.
 *
 * Ссылки, которые уже кому-то отправили, ломать незачем: путь стоит копейки, а
 * «страница не открылась» стоит доверия. Приведение к каноническому происходит
 * само и без записи в историю.
 */
const ALIASES: Record<string, Tab> = {
  '/': 'overview',
  '/obzor': 'overview',
  '/prilozheniya': 'services',
  '/topologiya': 'topology',
  '/agenty': 'agents',
  '/svyazi': 'links',
  '/diski': 'storage',
  '/nazvaniya': 'names',
  '/sobytiya': 'events',
  '/parametry': 'settings',
  '/klyuchi': 'devices',
};

const PATHS: Record<string, Tab> = {
  ...ALIASES,
  ...Object.fromEntries(
    (Object.entries(TABS) as [Tab, string][]).map(([tab, path]) => [path, tab] as const),
  ),
};

/** Перечень вкладок для проверок: список и тип обязаны совпадать. */
export const TAB_IDS = Object.keys(TABS) as Tab[];

export function tabFromPath(pathname: string): Tab | null {
  return PATHS[pathname.replace(/\/+$/, '') || '/'] ?? null;
}

export function pathForTab(tab: Tab): string {
  return TABS[tab];
}

/**
 * Вкладка, синхронизированная с адресом.
 *
 * `pushState` на переходах и `replaceState` на выравнивании неизвестного пути:
 * иначе кнопка «назад» уводила бы не на предыдущую вкладку, а с сайта.
 */
export function useTab(
  fallback: Tab,
  /** Путь, известный серверу: на нём `window` ещё нет. */
  ssrRoute?: string,
): [Tab, (next: Tab) => void] {
  const [tab, setTab] = useState(() => {
    const path = typeof window === 'undefined' ? ssrRoute : window.location.pathname;
    return (path ? tabFromPath(path) : null) ?? fallback;
  });

  useEffect(() => {
    const onPop = (): void => {
      setTab(tabFromPath(window.location.pathname) ?? fallback);
    };
    window.addEventListener('popstate', onPop);

    // Неизвестный путь выравниваем на канонический, но записи в историю НЕ
    // добавляем: пользователь по ней не ходил.
    if (tabFromPath(window.location.pathname) === null) {
      window.history.replaceState(null, '', pathForTab(fallback));
    }

    return () => window.removeEventListener('popstate', onPop);
  }, [fallback]);

  const go = (next: Tab): void => {
    setTab(next);
    const path = pathForTab(next);
    // Параметры прежней страницы не тащим: «площадка=helsinki» на экране
    // событий ничего не значит, а в адресе выглядит как настройка.
    if (window.location.pathname !== path || window.location.search) {
      window.history.pushState(null, '', path);
    }
  };

  return [tab, go];
}

/**
 * Состояние внутри страницы — в адресе, параметром запроса.
 *
 * Вкладка отвечает «какой раздел», параметр — «что в нём выбрано»: вид
 * топологии, площадка в матрице, фильтр. Держать это только в памяти значит
 * терять при обновлении ровно то, ради чего страницу открывали, и не иметь
 * возможности прислать ссылку на увиденное.
 *
 * Параметром, а не сегментом пути: у страницы таких состояний бывает
 * несколько, и городить `/topology/graph/helsinki` — значит однажды выбирать,
 * что из этого важнее и идёт первым.
 *
 * Значение по умолчанию из адреса УБИРАЕТСЯ: `?view=flows`, когда flows и так
 * первое, — это мусор, который расходится с тем же экраном, открытым без
 * параметра.
 */
// Две подписи: без белого списка параметр остаётся свободной строкой (площадка,
// группа), со списком — сужается до перечисленного. Одна общая подпись выводила
// бы тип из значения по умолчанию и делала `setGroup('helsinki')` ошибкой сборки
// там, где любая строка законна.
export function useParam(name: string, fallback: string): [string, (next: string) => void];
export function useParam<T extends string>(
  name: string,
  fallback: T,
  allowed: readonly T[],
): [T, (next: T) => void];
export function useParam<T extends string>(
  name: string,
  fallback: T,
  /**
   * Допустимые значения — там, где значение это выбор из известного набора.
   *
   * Площадка или группа — данные: какой пришёл, такой и берём. А подвкладка —
   * набор из трёх штук, и `?section=zzz` без проверки означает экран, на
   * котором не нарисовано ничего: ни одна ветка не совпала. Ссылку с опечаткой
   * или из прошлой версии консоли лучше открыть на разделе по умолчанию, чем
   * пустой страницей.
   */
  allowed?: readonly T[],
): [T, (next: T) => void] {
  const read = (): T => {
    if (typeof window === 'undefined') return fallback;
    const raw = new URLSearchParams(window.location.search).get(name);
    if (raw === null) return fallback;
    if (allowed && !allowed.includes(raw as T)) return fallback;
    return raw as T;
  };

  const [value, setValue] = useState(read);

  useEffect(() => {
    const onPop = (): void => setValue(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, fallback]);

  const set = (next: T): void => {
    setValue(next);
    const params = new URLSearchParams(window.location.search);
    if (next === fallback) params.delete(name);
    else params.set(name, next);
    const query = params.toString();
    window.history.pushState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  };

  return [value, set];
}
